import { useMemo, useState, useCallback } from 'react';
import { DataGrid, KeenIcon } from '@/components';
import { useSnackbar } from 'notistack';
import usePagination from '@/hooks/usePagination';
import { usePermissions } from '@/hooks/usePermissions';
import { ConfirmationDialog } from '@/components/dialogBox';
import {
  Testimonial,
  TestimonialListQueryParams,
  TestimonialStatus,
} from '@/types/testimonial.types';
import {
  useGetTestimonialsQuery,
  useDeleteTestimonialMutation,
} from '@/services/TestimonialService';
import { ErrorApiResponse } from '@/types/api.types';
import { useNavigate } from 'react-router-dom';
import { formatDisplayDate } from '@/utils/Date';

const formatDate = (iso?: string | null) => {
  if (!iso) return '';
  return formatDisplayDate(iso, '');
};

export const TestimonialsListing = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();
  const { enqueueSnackbar } = useSnackbar();

  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [confirmDeleteState, setConfirmDeleteState] = useState<{
    isOpen: boolean;
    testimonialId: string | null;
  }>({ isOpen: false, testimonialId: null });

  const queryParams: TestimonialListQueryParams = useMemo(() => {
    const params: TestimonialListQueryParams = {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
    };

    if (debouncedValue) params.search = debouncedValue;
    if (statusFilter === 'draft') {
      params.status = TestimonialStatus.DRAFT;
    } else if (statusFilter === 'published') {
      params.status = TestimonialStatus.PUBLISHED;
    }

    return params;
  }, [pagination.pageIndex, pagination.pageSize, debouncedValue, statusFilter]);

  const { data, isLoading, refetch } = useGetTestimonialsQuery(queryParams);
  const [deleteTestimonial] = useDeleteTestimonialMutation();

  const handleStatusFilterChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setStatusFilter(e.target.value);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [setPagination]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!confirmDeleteState.testimonialId) return;
    try {
      await deleteTestimonial(confirmDeleteState.testimonialId).unwrap();
      enqueueSnackbar('Testimonial deleted successfully', {
        variant: 'success',
      });
      setConfirmDeleteState({ isOpen: false, testimonialId: null });
      refetch();
    } catch (err) {
      const message =
        (err as ErrorApiResponse)?.data?.message ||
        'Failed to delete testimonial';
      enqueueSnackbar(message, { variant: 'error' });
    }
  }, [
    confirmDeleteState.testimonialId,
    deleteTestimonial,
    enqueueSnackbar,
    refetch,
  ]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'author_name',
        header: 'Author',
        enableSorting: false,
        cell: ({ row }: { row: { original: Testimonial } }) => {
          const t = row.original;
          return (
            <div className="flex items-center gap-3">
              {t.profile_image_url ? (
                <img
                  src={t.profile_image_url}
                  alt={t.author_name}
                  className="w-10 h-10 rounded-full object-cover border"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center border">
                  <KeenIcon icon="user" className="text-gray-500" />
                </div>
              )}
              <div className="flex flex-col">
                <button
                  type="button"
                  onClick={() => navigate(`/testimonial/edit/${t.id}`)}
                  className="text-sm font-medium text-gray-900 hover:text-primary-active text-left"
                  aria-label={`Edit testimonial from ${t.author_name}`}
                >
                  {t.author_name}
                </button>
                {t.author_title && (
                  <span className="text-xs text-gray-500">
                    {t.author_title}
                  </span>
                )}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: Testimonial } }) => {
          const status = row.original.status;
          const isPublished = status === TestimonialStatus.PUBLISHED;
          const badgeClass = isPublished
            ? 'bg-green-100 dark:bg-green-900 contrast:bg-green-900 text-green-800 dark:text-green-200 contrast:text-green-200'
            : 'bg-yellow-100 dark:bg-yellow-900 contrast:bg-yellow-900 text-yellow-800 dark:text-yellow-200 contrast:text-yellow-200';
          const label = isPublished ? 'Published' : 'Draft';
          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeClass}`}
            >
              {label}
            </span>
          );
        },
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        enableSorting: false,
        cell: ({ row }: { row: { original: Testimonial } }) => (
          <span className="text-xs text-gray-500">
            {formatDate(row.original.created_at)}
          </span>
        ),
      },
      {
        accessorKey: 'updated_at',
        header: 'Updated',
        enableSorting: false,
        cell: ({ row }: { row: { original: Testimonial } }) => (
          <span className="text-xs text-gray-500">
            {formatDate(row.original.updated_at)}
          </span>
        ),
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: Testimonial } }) => (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(`/testimonial/view/${row.original.id}`)}
              className="btn btn-sm btn-icon btn-light btn-outline"
              title="View"
              aria-label={`View testimonial from ${row.original.author_name}`}
            >
              <KeenIcon type="filled" icon="eye" className="text-gray-500" />
            </button>
            <button
              type="button"
              onClick={() => navigate(`/testimonial/edit/${row.original.id}`)}
              className="btn btn-sm btn-icon btn-light btn-outline"
              title="Edit"
              aria-label={`Edit testimonial from ${row.original.author_name}`}
            >
              <KeenIcon type="filled" icon="pencil" className="text-gray-500" />
            </button>
            {hasPermission('cms_management_update') && (
              <button
                type="button"
                onClick={() =>
                  setConfirmDeleteState({
                    isOpen: true,
                    testimonialId: row.original.id,
                  })
                }
                className="btn btn-sm btn-icon btn-light btn-outline"
                title="Delete"
                aria-label={`Delete testimonial from ${row.original.author_name}`}
              >
                <KeenIcon type="filled" icon="trash" className="text-red-500" />
              </button>
            )}
          </div>
        ),
        meta: { className: 'w-[120px]' },
      },
    ],
    [hasPermission, navigate]
  );

  const testimonials = useMemo(() => data?.data ?? [], [data]);

  if (data) {
    const totalPagesValue = data.totalPages ?? data.total_pages ?? 1;
    const totalItemsValue =
      data.totalItems ?? data.total_items ?? data.total ?? 0;
    if (totalPagesValue !== totalPages) {
      handleTotalPages(totalPagesValue);
    }
    if (totalItemsValue !== totalItems) {
      handleTotalItems(totalItemsValue);
    }
  }

  return (
    <>
      <div className="card">
        <div className="card-header flex flex-wrap items-center gap-4 justify-between w-full">
          <div className="flex items-center gap-4">
            <label
              className="input input-sm h-8 sm:h-10 flex items-center"
              htmlFor="search-testimonials"
              aria-label="Search testimonials by author or content"
            >
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="text-gray-500"
              />
              <input
                id="search-testimonials"
                placeholder="Search testimonials"
                type="text"
                value={searchTerm || ''}
                onChange={handleSearch}
                className="w-48 sm:w-64 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                aria-label="Search testimonials by author or content"
              />
            </label>

            <select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              className="select select-sm w-40"
              aria-label="Filter testimonials by status"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>

          {(searchTerm && searchTerm.trim() !== '') ||
          statusFilter !== 'all' ? (
            <button
              className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10"
              onClick={() => {
                if (searchTerm && searchTerm.trim() !== '') resetSearch();
                setStatusFilter('all');
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              aria-label="Clear all filters"
            >
              <KeenIcon
                type="solid"
                icon="arrows-circle"
                className="text-gray-600"
              />
              Clear Filters
            </button>
          ) : null}
        </div>
        <div className="card-body">
          <DataGrid
            columns={columns}
            data={testimonials}
            loading={isLoading}
            rowSelect={false}
            pagination={{
              page: pagination.pageIndex,
              size: pagination.pageSize,
              more: true,
              totalItems,
              totalPages,
              setPagination,
              info: `{from} - {to} of {count}`,
              sizes: [5, 10, 25, 50, 100],
              sizesLabel: 'Show',
              sizesDescription: 'per page',
            }}
            serverSide={true}
          />
        </div>
      </div>

      <ConfirmationDialog
        open={confirmDeleteState.isOpen}
        onClose={() =>
          setConfirmDeleteState({ isOpen: false, testimonialId: null })
        }
        onConfirm={handleConfirmDelete}
        message="Are you sure you want to delete this testimonial? This action cannot be undone."
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
      />
    </>
  );
};
